import React, { useEffect, useState } from "react";
import { listAll, createDoc, updateDocById, removeDoc } from "../../services/firestore";
import { autoTranslatePatch } from "../../services/translation";
import { uploadImageToR2 } from "../../services/r2";
import { Plus, Trash2, Edit3, X, Upload, Image as ImageIcon } from "lucide-react";

const EMPTY = { name: "", slug: "", bio: "", image_url: "", featured: false };

function slugify(s) {
  return (s || "").toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

export default function AdminArtists() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const [uploading, setUploading] = useState(false);

  const reload = async () => setItems(await listAll("artists"));
  useEffect(() => { reload(); }, []);

  const open = (it) => { setEditing(it ? { ...it } : { ...EMPTY }); setUploadErr(""); };
  const close = () => setEditing(null);
  const onName = (v) => setEditing((s) => ({ ...s, name: v, slug: s.slug || slugify(v) }));

  const save = async () => {
    if (!editing.name) return alert("Sənətçi adını yazın.");
    setBusy(true);
    try {
      const i18n = await autoTranslatePatch({ bio: editing.bio || "" });
      const data = { ...editing, ...i18n, slug: editing.slug || slugify(editing.name) };
      if (editing.id) await updateDocById("artists", editing.id, data);
      else await createDoc("artists", data);
      await reload(); close();
    } finally { setBusy(false); }
  };

  const del = async (it) => {
    if (!window.confirm(`'${it.name}' silinsin?`)) return;
    await removeDoc("artists", it.id); reload();
  };

  const onPickImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true); setUploadErr("");
    try {
      const url = await uploadImageToR2(f, { folder: "artists" });
      setEditing((s) => ({ ...s, image_url: url }));
    } catch (ex) { setUploadErr(ex.message); }
    finally { setUploading(false); e.target.value = ""; }
  };

  return (
    <div className="p-10" data-testid="admin-artists">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="metadata">İNSANLAR</div>
          <h1 className="font-display text-5xl mt-1">Sənətçilər</h1>
        </div>
        <button onClick={() => open(null)} className="btn-primary" data-testid="add-artist"><Plus size={14} /> SƏNƏTÇİ ƏLAVƏ ET</button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((a) => (
          <div key={a.id} className="border border-[var(--border)] p-4 flex gap-3 group" data-testid={`artist-${a.id}`}>
            {a.image_url ? <img src={a.image_url} alt="" className="w-20 h-20 object-cover" /> : <div className="w-20 h-20 bg-[var(--surface)] flex items-center justify-center"><ImageIcon size={20} className="text-[var(--text-muted)]" /></div>}
            <div className="flex-1 min-w-0">
              <div className="font-display text-2xl truncate">{a.name}</div>
              <div className="text-sm text-[var(--text-2)] line-clamp-2 mt-1">{a.bio}</div>
            </div>
            <div className="flex flex-col gap-2">
              <button onClick={() => open(a)} className="p-1 hover:text-[var(--primary)]"><Edit3 size={14} /></button>
              <button onClick={() => del(a)} className="p-1 hover:text-[var(--accent)]"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {!items.length && <div className="col-span-full p-10 text-center text-[var(--text-muted)] metadata border border-[var(--border)]">HƏLƏ SƏNƏTÇİ YOXDUR</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6" onClick={close}>
          <div className="bg-[var(--bg)] border border-[var(--border)] w-full max-w-xl p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display text-3xl">{editing.id ? "Düzəliş" : "Yeni"} Sənətçi</h2>
              <button onClick={close}><X /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="metadata">AD <span className="text-[var(--text-muted)] normal-case">— sənətçinin adı</span></label>
                <input className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1" value={editing.name} onChange={(e) => onName(e.target.value)} data-testid="art-name" placeholder="Məsələn: Eljan Nagi" />
              </div>
              <div><label className="metadata">BİO <span className="text-[var(--text-muted)] normal-case">— sənətçi haqqında</span></label><textarea rows={3} className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1" value={editing.bio} onChange={(e) => setEditing({ ...editing, bio: e.target.value })} /></div>
              <div>
                <label className="metadata">ŞƏKİL</label>
                <div className="mt-1 flex items-center gap-3">
                  {editing.image_url ? <img src={editing.image_url} alt="" className="w-16 h-16 object-cover border border-[var(--border)]" /> : <div className="w-16 h-16 bg-[var(--surface)]" />}
                  <label className="btn-outline cursor-pointer">
                    <Upload size={14} /> {uploading ? "…" : "R2-yə yüklə"}
                    <input type="file" accept="image/*" hidden onChange={onPickImage} />
                  </label>
                  <input className="flex-1 bg-transparent border border-[var(--border)] px-3 py-2 font-mono text-xs" placeholder="şəkil URL" value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} />
                </div>
                {uploadErr && <div className="text-[var(--accent)] text-sm mt-2">{uploadErr}</div>}
              </div>
              <label className="flex items-center gap-2 metadata"><input type="checkbox" checked={!!editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} /> SEÇİLMİŞ</label>
            </div>
            <div className="flex justify-end gap-3 mt-8">
              <button onClick={close} className="btn-outline">LƏĞV ET</button>
              <button onClick={save} disabled={busy} className="btn-primary" data-testid="art-save">{busy ? "…" : "YADDA SAXLA"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
